import React, { useCallback } from "react";
import { Modal } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";


import {
	Button,
	CloseButton,
	HookSelectFormGroup,
	OnlineLink,
	Skeleton,
} from "components";
import { DebounceTimeout } from "globalConstants";
import { useLoader } from "hooks";
import {
	useGetResidualsSplitProfileQuery,
	useUpdateSplitProfileDefaultMutation,
} from "queries";
import { required } from "validators/validation";

import { ReactComponent as WarningIcon } from "static/images/warning-alert.svg";
import styles from "./reassignModal.module.scss";

type ReassignDefaultResidualsSplitProfileModalProps = {
	show: boolean;
	onHide: () => void;
	agentId: string;
	splitProfileId: string;
	warningMessage: string;
	onSuccess?: Function;
	isOpenPageInNewTab?: boolean;
};

const ReassignDefaultResidualsSplitProfileModal = ({
	show = false,
	onHide,
	agentId,
	splitProfileId,
	warningMessage,
	onSuccess,
	isOpenPageInNewTab = false,
}: ReassignDefaultResidualsSplitProfileModalProps) => {
	const formMethods = useForm({
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: {},
	});

	const {
		handleSubmit,
		formState: { isDirty },
	} = formMethods;

	const { data: residualsSplitProfiles, isFetching: isLoadingSplitProfiles } =
		useGetResidualsSplitProfileQuery({ agentId });

	const { mutateAsync: setDefault } = useUpdateSplitProfileDefaultMutation();

	const residualsSplitProfilesExceptionCurrent =
		residualsSplitProfiles?.filter(({ value }) => value !== splitProfileId);
	const isEmptySplitProfiles =
		residualsSplitProfilesExceptionCurrent?.length === 0;
	const urlToNewResidualsSplitProfile = `/system-setup/agent-setup/edit-agent/${agentId}/residuals-split-profiles/add`;

	const Loader = useLoader(isLoadingSplitProfiles);

	const handleSubmitForm = useCallback(
		async ({ splitProfileList }) => {
			onHide();
			await setDefault({
				agentId,
				splitProfileId: splitProfileList.value,
				isDefault: true,
			});
			onSuccess?.();
		},
		[agentId, onHide, onSuccess, setDefault],
	);

	return (
		<Modal show={show} onHide={onHide} animation={false} centered>
			<Loader
				skeleton={<Skeleton height={200} />}
				component={
					<FormProvider {...formMethods}>
						<form onSubmit={handleSubmit(handleSubmitForm)}>
							<Modal.Header className="pb-0">
								<Modal.Title as="h6" className="text-center">
									{isEmptySplitProfiles ? (
										<div className="text-warning">
											<WarningIcon width="30" />
										</div>
									) : (
										<div className="mb-3">
											Default Residuals Split Profile
										</div>
									)}
								</Modal.Title>
								<CloseButton onClick={onHide} />
							</Modal.Header>
							<Modal.Body>
								<div className={styles.content}>
									{isEmptySplitProfiles ? (
										<div className={styles.warningMessage}>
											{warningMessage}
										</div>
									) : (
										<>
											<div className="text-center mb-3">
												Please reassign default Split
												Profile:
											</div>
											<HookSelectFormGroup
												label="Residuals Split Profile"
												options={
													residualsSplitProfilesExceptionCurrent
												}
												getOptionLabel={option =>
													option.name
												}
												getOptionValue={option =>
													option.value
												}
												placeholder="Please Select"
												name="splitProfileList"
												isSearchable={false}
												cacheOptions
												defaultOptions
												defaultValue={null}
												debounceTimeout={
													DebounceTimeout
												}
												blurLoading={
													isLoadingSplitProfiles
												}
												colLabel={{ sm: 12 }}
												colSelect={{ sm: 12 }}
												hideLabel
												brandingColor
												containerClassName={
													styles.ddlContainer
												}
												rules={{
													validate: {
														required,
													},
												}}
											/>
										</>
									)}
								</div>
							</Modal.Body>
							<Modal.Footer className="justify-content-center pb-5">
								<Button
									variant="outline-primary"
									value="Cancel"
									onClick={onHide}
									className="mr-4"
								/>
								{isEmptySplitProfiles ? (
									<Button
										value="Add New"
										variant="primary"
										to={urlToNewResidualsSplitProfile}
										as={OnlineLink}
										onClick={onHide}
										target={
											isOpenPageInNewTab
												? "_blank"
												: "_self"
										}
									/>
								) : (
									<Button
										type="submit"
										value="Save"
										variant="primary"
										disabled={!isDirty}
									/>
								)}
							</Modal.Footer>
						</form>
					</FormProvider>
				}
			/>
		</Modal>
	);
};

export default ReassignDefaultResidualsSplitProfileModal;
